<template>
  <div class="module-rotation-calendar">
    <!-- Calendar Header -->
    <div class="flex items-center justify-between mb-6">
      <div>
        <h3 class="text-xl font-semibold text-primary-text dark:text-primary-dark-text">
          Module Rotation Calendar
        </h3>
        <p class="text-sm text-secondary-text dark:text-secondary-dark-text mt-1">
          View training module schedule for {{ currentYear }}
        </p>
      </div>
      <div class="flex items-center space-x-2">
        <button
          @click="changeYear(-1)"
          class="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          :disabled="loading"
        >
          <ChevronLeft class="w-4 h-4 text-gray-600 dark:text-gray-300" />
        </button>
        <span class="text-lg font-medium text-primary-text dark:text-primary-dark-text min-w-[80px] text-center">
          {{ currentYear }}
        </span>
        <button
          @click="changeYear(1)"
          class="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          :disabled="loading"
        >
          <ChevronRight class="w-4 h-4 text-gray-600 dark:text-gray-300" />
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center items-center py-12">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      <span class="ml-3 text-primary-text dark:text-primary-dark-text">Loading module rotations...</span>
    </div>

    <!-- Error State -->
    <div
      v-else-if="error"
      class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4"
    >
      <div class="flex items-center">
        <AlertCircle class="w-5 h-5 text-red-500 mr-2" />
        <span class="text-red-700 dark:text-red-300">{{ error }}</span>
      </div>
      <button
        @click="fetchRotations"
        class="mt-2 text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 underline"
      >
        Try again
      </button>
    </div>

    <!-- Calendar Grid -->
    <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      <div
        v-for="month in monthsData"
        :key="month.number"
        class="calendar-month relative border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden bg-white dark:bg-gray-800 hover:shadow-xl hover:scale-[1.02] transition-all duration-300 cursor-pointer group"
        @click="openMonthModal(month)"
      >
        <!-- Month Header -->
        <div 
          class="px-5 py-4 border-b border-gray-200 dark:border-gray-700 relative"
          :class="month.isCurrentMonth ? 'bg-primary/10 dark:bg-primary-dark/10' : 'bg-gray-50 dark:bg-gray-700'"
        >
          <div class="flex items-center justify-between min-w-0">
            <h4 class="font-semibold text-primary-text dark:text-primary-dark-text text-lg truncate">
              {{ month.name }}
            </h4>

          </div>
          <!-- Subtle gradient overlay for visual depth -->
          <div class="absolute inset-0 bg-gradient-to-r from-transparent to-white/5 dark:to-gray-800/5 pointer-events-none"></div>
        </div>

        <!-- Module Content -->
        <div class="p-5">
          <div v-if="month.module" class="space-y-4">
            <!-- Module Info -->
            <div class="flex items-center space-x-3">
              <div
                class="flex-shrink-0 w-4 h-4 rounded-full shadow-sm border-2 border-white dark:border-gray-800"
                :class="getModuleStatusColor(month.module)"
                aria-hidden="true"
              ></div>
              <div class="flex-1 min-w-0">
                <h5 class="font-semibold text-primary-text dark:text-primary-dark-text truncate text-base group-hover:text-primary transition-colors">
                  {{ month.module.module_name }}
                </h5>
                <p
                  class="text-sm mt-1 font-medium"
                  :class="getModuleStatusTextColor(month.module)"
                >
                  {{ getModuleStatusText(month.module) }}
                </p>
              </div>
            </div>

            <!-- Date Range -->
            <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 border border-gray-100 dark:border-gray-600">
              <div class="flex items-center text-sm text-secondary-text dark:text-secondary-dark-text">
                <Calendar class="w-4 h-4 mr-2" />
                <span class="font-medium">{{ formatDateRange(month.module.start_date, month.module.end_date) }}</span>
              </div>
            </div>

            <!-- Time Progress Indicator -->
            <div class="w-full">
              <div class="flex justify-between text-xs text-secondary-text dark:text-secondary-dark-text mb-2">
                <span class="font-medium">Progress</span>
                <span class="font-semibold">{{ getMonthProgress(month.module) }}%</span>
              </div>
              <div class="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2.5 shadow-inner">
                <div 
                  class="h-2.5 rounded-full transition-all duration-500 ease-out shadow-sm"
                  :class="getProgressBarColor(month.module)"
                  :style="{ width: getMonthProgress(month.module) + '%' }"
                ></div>
              </div>
            </div>
          </div>

          <!-- No Module Assigned -->
          <div v-else class="text-center py-8">
            <div class="bg-gray-100 dark:bg-gray-700 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3">
              <Calendar class="w-8 h-8 text-gray-400 dark:text-gray-500" />
            </div>
            <p class="text-sm text-secondary-text dark:text-secondary-dark-text font-medium">
              No module assigned
            </p>
            <p class="text-xs text-gray-400 dark:text-gray-500 mt-1">
              Available for assignment
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Legend -->
    <div class="mt-8 p-5 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
      <h4 class="text-sm font-semibold text-primary-text dark:text-primary-dark-text mb-4">Status Legend</h4>
      <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div class="flex items-center">
          <div class="w-4 h-4 rounded-full bg-green-500 mr-3 shadow-sm border-2 border-white dark:border-gray-800"></div>
          <span class="text-sm text-secondary-text dark:text-secondary-dark-text font-medium">Current Module</span>
        </div>
        <div class="flex items-center">
          <div class="w-4 h-4 rounded-full bg-blue-500 mr-3 shadow-sm border-2 border-white dark:border-gray-800"></div>
          <span class="text-sm text-secondary-text dark:text-secondary-dark-text font-medium">Upcoming Module</span>
        </div>
        <div class="flex items-center">
          <div class="w-4 h-4 rounded-full bg-gray-400 mr-3 shadow-sm border-2 border-white dark:border-gray-800"></div>
          <span class="text-sm text-secondary-text dark:text-secondary-dark-text font-medium">Past Module</span>
        </div>
      </div>
    </div>

    <!-- Module Details Modal -->
    <ModuleDetailsModal
      :show="showModal"
      :month-data="selectedMonth"
      :year="currentYear"
      @close="closeModal"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue'
import { ModulesService, type ModuleRotation } from '@/services/modules'
import ModuleDetailsModal from '@/components/modals/ModuleDetailsModal.vue'
import { 
  ChevronLeft, 
  ChevronRight, 
  Calendar, 
  AlertCircle
} from 'lucide-vue-next'

// Define month interface
interface MonthData {
  number: number
  name: string
  module: ModuleRotation | null
  isCurrentMonth: boolean
}

// Reactive data
const currentYear = ref(new Date().getFullYear())
const rotations = ref<ModuleRotation[]>([])
const loading = ref(false)
const error = ref<string | null>(null)
const showModal = ref(false)
const selectedMonth = ref<MonthData | null>(null)

// Month names
const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
]

// Computed properties
const monthsData = computed(() => {
  const currentDate = new Date()
  const currentMonth = currentDate.getMonth() + 1
  const currentYearActual = currentDate.getFullYear()
  
  return monthNames.map((name, index) => {
    const monthNumber = index + 1
    const moduleForMonth = rotations.value.find(rotation => 
      rotation.month === monthNumber && rotation.year === currentYear.value
    )
    
    return {
      number: monthNumber,
      name,
      module: moduleForMonth || null,
      isCurrentMonth: currentYear.value === currentYearActual && monthNumber === currentMonth
    }
  })
})

// Methods
const fetchRotations = async () => {
  loading.value = true
  error.value = null
  
  try {
    rotations.value = await ModulesService.getModuleRotations(currentYear.value)
  } catch (err: any) {
    error.value = err.message || 'Failed to fetch module rotations'
  } finally {
    loading.value = false
  }
}

const changeYear = (delta: number) => {
  currentYear.value += delta
}

const getModuleStatusColor = (module: ModuleRotation): string => {
  if (module.is_current) return 'bg-green-500'
  if (module.is_future) return 'bg-blue-500'
  return 'bg-gray-400'
}

const getModuleStatusTextColor = (module: ModuleRotation): string => {
  if (module.is_current) return 'text-green-600 dark:text-green-400'
  if (module.is_future) return 'text-blue-600 dark:text-blue-400'
  return 'text-gray-500 dark:text-gray-400'
}

const getModuleStatusText = (module: ModuleRotation): string => {
  if (module.is_current) return 'Current Module'
  if (module.is_future) return 'Upcoming Module'
  return 'Past Module'
}

const getProgressBarColor = (module: ModuleRotation): string => {
  if (module.is_current) return 'bg-green-500'
  if (module.is_future) return 'bg-blue-500'
  return 'bg-gray-400'
}

const formatDateRange = (startDate: string, endDate: string): string => {
  const start = new Date(startDate)
  const end = new Date(endDate)
  
  const startDay = start.getDate()
  const endDay = end.getDate()
  const month = start.toLocaleDateString('en-US', { month: 'short' })
  
  return `${month} ${startDay}-${endDay}`
}

const getMonthProgress = (module: ModuleRotation): number => {
  if (module.is_past) return 100
  if (module.is_future) return 0
  
  // For current module, calculate actual progress
  const start = new Date(module.start_date)
  const end = new Date(module.end_date)
  const now = new Date()
  
  const total = end.getTime() - start.getTime()
  const elapsed = now.getTime() - start.getTime()
  
  return Math.max(0, Math.min(100, Math.round((elapsed / total) * 100)))
}

// Modal methods
const openMonthModal = (month: MonthData) => {
  selectedMonth.value = month
  showModal.value = true
}

const closeModal = () => {
  showModal.value = false
  selectedMonth.value = null
}

// Watch for year changes
watch(currentYear, () => {
  fetchRotations()
})

// Initialize
onMounted(() => {
  fetchRotations()
})
</script>

<style scoped>
.calendar-month {
  min-height: 240px;
}

.module-rotation-calendar {
  width: 100%;
  height: 100%;
}

/* Enhanced hover effect */
.calendar-month:hover {
  transform: translateY(-2px);
}

/* Smooth transitions for interactive elements */
.calendar-month * {
  transition: all 0.2s ease;
}

/* Enhanced focus styles for accessibility */
.calendar-month:focus {
  outline: 2px solid rgba(59, 130, 246, 0.5);
  outline-offset: 2px;
}

/* Improved status indicator visibility */
.calendar-month .w-4.h-4.rounded-full {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Local truncate utility to ensure ellipsis works inside scoped styles */
.truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
